<template>
	<div class="datagrid-pagination">
		<div class="left">
			<div class="visible-range">{{ visibleRange }}</div>

			<VSelect
				:options="limitOptions"
				option-label="name"
				option-identity="value"
				:clearable="false"
				:model-value="currentLimitOption"
				@update:modelValue="onLimitChange"
			/>
		</div>

		<div class="right">
			<button
				v-for="page in firstPageButtons"
				:key="'first_' + page"
				class="page-btn"
				:class="{ current: page === currentPage }"
				@click="goToPage(page)"
			>
				{{ page }}
			</button>

			<button
				class="nav-btn"
				:disabled="isFirstPage"
				title="Назад"
				@click="goToPage(currentPage - 1)"
			>
				<VIcon material="keyboard_arrow_left"/>
			</button>

			<div class="current-info">
				страница {{ currentPage }} из {{ pagesTotal }}
			</div>

			<button
				class="nav-btn"
				:disabled="isLastPage"
				title="Вперёд"
				@click="goToPage(currentPage + 1)"
			>
				<VIcon material="keyboard_arrow_right"/>
			</button>

			<button
				v-if="showLastPageButton"
				class="page-btn"
				:class="{ current: pagesTotal === currentPage }"
				@click="goToPage(pagesTotal)"
			>
				{{ pagesTotal }}
			</button>
		</div>
	</div>
</template>

<style lang="scss" src="./VDataGridPaginationV2.scss"/>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import VSelect                       from '@/lib/components/VSelect/VSelect.vue';

import type RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';

/**
 * Допустимые значения количества элементов на странице.
 * Если приходящий pagination.limit не входит в этот список —
 * добавляется первым элементом (см. limitOptions).
 */
const LIMIT_OPTIONS = [25, 50, 100];

type LimitOption = {
	name:  string;
	value: number;
};

export default defineComponent({
	name: 'VDataGridPaginationV2',

	components: { VSelect },

	props: {
		pagination: {
			type:     Object as PropType<RpcDataPagination>,
			required: true,
		},
	},

	emits: ['update:pagination'],

	computed: {
		currentPage(): number {
			return Math.floor(this.pagination.offset / this.pagination.limit) + 1;
		},

		pagesTotal(): number {
			return Math.max(1, Math.ceil(this.pagination.total / this.pagination.limit));
		},

		isFirstPage(): boolean {
			return this.currentPage === 1;
		},

		isLastPage(): boolean {
			return this.currentPage >= this.pagesTotal;
		},

		visibleRange(): string {
			const start = this.pagination.offset + 1;
			const end   = Math.min(this.pagination.offset + this.pagination.limit, this.pagination.total);
			return `${start}-${end} из ${this.pagination.total}`;
		},

		/**
		 * Кнопки первых страниц [1] [2] [3].
		 * Раскладка зависит только от pagesTotal — при навигации не меняется.
		 */
		firstPageButtons(): number[] {
			const count = Math.min(3, this.pagesTotal);
			return Array.from({ length: count }, (_, i) => i + 1);
		},

		/**
		 * Кнопка "последняя страница" нужна только когда страниц больше трёх.
		 * При pagesTotal <= 3 она дублировала бы одну из firstPageButtons.
		 */
		showLastPageButton(): boolean {
			return this.pagesTotal > 3;
		},

		/**
		 * Опции селектора лимитов.
		 * Если текущий limit не входит в LIMIT_OPTIONS — добавляем его первым,
		 * чтобы селектор не показывал рассинхрон с фактическим значением.
		 */
		limitOptions(): LimitOption[] {
			const values = LIMIT_OPTIONS.includes(this.pagination.limit)
				? LIMIT_OPTIONS
				: [this.pagination.limit, ...LIMIT_OPTIONS];

			return values.map(value => ({
				name: `Показывать по ${value}`,
				value,
			}));
		},

		currentLimitOption(): LimitOption | undefined {
			return this.limitOptions.find(option => option.value === this.pagination.limit);
		},
	},

	methods: {
		goToPage(page: number): void {
			if (page < 1 || page > this.pagesTotal || page === this.currentPage) {
				return;
			}

			const offset = (page - 1) * this.pagination.limit;

			this.$emit('update:pagination', {
				limit:  this.pagination.limit,
				offset,
			});
		},

		onLimitChange(option: LimitOption): void {
			this.$emit('update:pagination', {
				limit:  option.value,
				offset: 0,
			});
		},
	},
});
</script>